<script setup lang="ts">
import { computed } from 'vue';
import CopyIcon from 'src/shared/icons/copy.svg';
import type { TypographyVariant } from 'src/shared/ui';

export type ClientInfoIconSize = 's' | 'm' | 'mplus' | 'l' | 'xl';

export interface ClientInfoProps {
  title?: string;
  subtitle?: string;
  titleVariant?: TypographyVariant;
  subtitleVariant?: TypographyVariant;
  iconSize?: ClientInfoIconSize;
  clickable?: boolean;
  copyValue?: boolean;
  cbClick?: () => void;
  cbClickMiddle?: () => void;
}

const props = withDefaults(defineProps<ClientInfoProps>(), {
  title: '#',
  subtitle: '#',
  titleVariant: 'caption-2',
  subtitleVariant: 'body-1',
  iconSize: 'm',
});

const iconSizeStyle = computed(() => ({
  width: `var(--icon-size-${props.iconSize})`,
  height: `var(--icon-size-${props.iconSize})`,
}));

function handleValueClick(): void {
  if ((props.clickable || props.copyValue) && props.cbClick) {
    props.cbClick();
  }
}

function handleValueMiddleClick(): void {
  if (props.clickable && props.cbClickMiddle) {
    props.cbClickMiddle();
  }
}
</script>

<template>
  <div class="container">
    <typography :variant="props.titleVariant" as="p" :class="'title'">{{ props.title }}</typography>

    <typography
      :variant="props.subtitleVariant"
      as="p"
      :class="['subtitle', { 'as-link': props.clickable }]"
    >
      <div
        class="flex items-center value"
        :class="{ copyable: props.copyValue }"
        @click="handleValueClick"
        @click.middle="handleValueMiddleClick"
      >
        <span><slot name="subtitle">{{ props.subtitle }}</slot></span>
        <CopyIcon v-if="props.copyValue" class="copy-icon" :style="iconSizeStyle" />
      </div>
    </typography>
  </div>
</template>

<style scoped>
.container {
  overflow: hidden;
  display: flex;

  flex-direction: column;
}

.title {
  color: var(--palette-graphite);
}

.subtitle {
  white-space: break-spaces;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
}

.copy-icon {
  color: var(--palette-gray-blue);
}

.copyable:hover {
  .copy-icon {
    color: var(--palette-blue);
  }
}

.value {
  gap: var(--spacing-xs);
}

.copyable {
  width: fit-content;
  max-width: 100%;
  cursor: pointer;
}

.as-link {
  color: var(--palette-blue-light);
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    color: var(--palette-blue);
  }
}

@media screen and (max-width: 755px) {
  .container {
    min-width: 0;
    gap: 3px;
    overflow: visible;
  }

  .title {
    font-size: 11px;
    line-height: 15px;
  }

  .subtitle {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    overflow-wrap: anywhere;
    word-break: normal;
    line-height: 19px;
  }

  .value {
    min-width: 0;
    align-items: flex-start;

    & > span {
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }
}
</style>
